import * as Utils from "../Utils";
import { adminFetch, buildQueryString } from "./admin_api";
import { getAdminToken } from "./auth_helper";
import { normalizePagination } from "./affiliate_helper";
import { formatProductPickerLabel } from "./review_helper";

export const API_NOT_DEPLOYED_MSG =
  "Skin Assistant API returned 404. The route is not deployed on the API server this admin app is using yet.";

const getAuthToken = () => getAdminToken();

const authHeaders = (token = getAuthToken()) => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: token ? `Bearer ${token}` : "",
});

const rulesBase = () => `${Utils.API_URL}v1/skin-assistant/rules`;
const adminBase = () => `${Utils.API_URL}v1/admin/skin-assistant`;

/** GET /api/v1/skin-assistant/rules */
export async function getSkinAssistantRules(token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  return adminFetch(rulesBase(), {
    method: "GET",
    headers: authHeaders(token),
  });
}

/** PUT /api/v1/skin-assistant/rules (admin) */
export async function putSkinAssistantRules(payload, token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  return adminFetch(rulesBase(), {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
}

/** GET /api/v1/admin/skin-assistant/results */
export async function getSkinAssistantResults(params = {}, token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  return adminFetch(`${adminBase()}/results${buildQueryString(params)}`, {
    method: "GET",
    headers: authHeaders(token),
  });
}

/** GET /api/v1/admin/skin-assistant/leads */
export async function getSkinAssistantLeads(params = {}, token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  return adminFetch(`${adminBase()}/leads${buildQueryString(params)}`, {
    method: "GET",
    headers: authHeaders(token),
  });
}

/** POST /api/v1/admin/skin-assistant/knowledge/reindex */
export async function reindexSkinAssistantKnowledge(token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  return adminFetch(`${adminBase()}/knowledge/reindex`, {
    method: "POST",
    headers: authHeaders(token),
  });
}

/** GET /api/v1/admin/skin-assistant/chats */
export async function getSkinAssistantChats(params = {}, token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  return adminFetch(`${adminBase()}/chats${buildQueryString(params)}`, {
    method: "GET",
    headers: authHeaders(token),
  });
}

/** DELETE /api/v1/admin/skin-assistant/leads/:id */
export async function deleteSkinAssistantLead(leadId, token = getAuthToken()) {
  if (!token) {
    return Promise.resolve({
      success: false,
      message: "Not Authorized - No Token Provided",
    });
  }
  const id = encodeURIComponent(String(leadId));
  return adminFetch(`${adminBase()}/leads/${id}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
}

export const SKIN_TYPE_OPTIONS = [
  { value: "oily", label: "Oily" },
  { value: "dry", label: "Dry" },
  { value: "combination", label: "Combination" },
  { value: "normal", label: "Normal" },
  { value: "sensitive", label: "Sensitive" },
];

export const CONCERN_OPTIONS = [
  { value: "acne", label: "Acne & breakouts" },
  { value: "pigmentation", label: "Pigmentation" },
  { value: "aging", label: "Fine lines & aging" },
  { value: "dullness", label: "Dullness" },
  { value: "dehydration", label: "Dehydration" },
  { value: "redness", label: "Redness" },
  { value: "pores", label: "Large pores" },
  { value: "dark_circles", label: "Dark circles" },
];

export const BUDGET_OPTIONS = [
  { value: "low", label: "Budget-friendly" },
  { value: "mid", label: "Mid-range" },
  { value: "high", label: "Premium" },
];

export const AGE_BAND_OPTIONS = [
  { value: "under_25", label: "Under 25" },
  { value: "25_34", label: "25–34" },
  { value: "35_44", label: "35–44" },
  { value: "45_plus", label: "45+" },
];

export const SENSITIVITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const USES_SPF_OPTIONS = [
  { value: "", label: "Any" },
  { value: "true", label: "Uses SPF" },
  { value: "false", label: "Does not use SPF" },
];

export const LEAD_CHANNEL_OPTIONS = [
  { value: "web", label: "Web" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "instagram", label: "Instagram" },
  { value: "other", label: "Other" },
];

const normalizeIdList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === "object") {
        return String(item._id || item.id || item.productId || "");
      }
      return String(item);
    }).filter(Boolean);
  }
  return [];
};

const normalizeMatch = (match = {}) => {
  const usesSpfRaw = match.usesSpf;
  let usesSpf = "";
  if (usesSpfRaw === true || usesSpfRaw === "true") usesSpf = "true";
  else if (usesSpfRaw === false || usesSpfRaw === "false") usesSpf = "false";

  return {
    skinType: match.skinType ? String(match.skinType) : "",
    concern: match.concern ? String(match.concern) : "",
    budget: match.budget ? String(match.budget) : "",
    ageBand: match.ageBand ? String(match.ageBand) : "",
    sensitivity: match.sensitivity ? String(match.sensitivity) : "",
    usesSpf,
  };
};

const normalizeRoutine = (routine = {}, legacy = {}) => {
  const am = normalizeIdList(routine.am ?? routine.AM ?? legacy.amRoutine ?? legacy.am);
  const pm = normalizeIdList(routine.pm ?? routine.PM ?? legacy.pmRoutine ?? legacy.pm);
  return { am, pm };
};

export const normalizeRule = (raw, index = 0) => {
  if (!raw || typeof raw !== "object") {
    return createEmptyRule(index);
  }
  const match = normalizeMatch(raw.match || raw.conditions || raw.answers || raw);
  const routine = normalizeRoutine(raw.routine, raw);
  return {
    id: raw.id || raw._id || `rule-${index + 1}`,
    match,
    recommendedProductIds: normalizeIdList(
      raw.recommendedProductIds ?? raw.productIds ?? raw.recommendedProducts
    ),
    routine,
  };
};

export const createEmptyRule = (index = 0) => ({
  id: `rule-${Date.now()}-${index}`,
  match: {
    skinType: "",
    concern: "",
    budget: "",
    ageBand: "",
    sensitivity: "",
    usesSpf: "",
  },
  recommendedProductIds: [],
  routine: { am: [], pm: [] },
});

const extractRulesArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rules)) return payload.rules;
  if (Array.isArray(payload.ruleSet)) return payload.ruleSet;
  if (Array.isArray(payload.data?.rules)) return payload.data.rules;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    if (Array.isArray(payload.data.ruleSet)) return payload.data.ruleSet;
  }
  return [];
};

export const parseRulesResponse = (res) => {
  if (!res) {
    return { rules: [], version: null, updatedAt: null, updatedBy: null, error: "Empty response" };
  }
  if (res.success === false) {
    return {
      rules: [],
      version: null,
      updatedAt: null,
      updatedBy: null,
      error: res.message || "Failed to load rules",
    };
  }

  const root = res.data && typeof res.data === "object" ? res.data : res;
  const rules = extractRulesArray(root).map((rule, index) => normalizeRule(rule, index));

  return {
    rules,
    version: root.version ?? root.rulesVersion ?? res.version ?? null,
    updatedAt: root.updatedAt ?? root.updated_at ?? null,
    updatedBy:
      root.updatedBy?.email ||
      root.updatedBy?.name ||
      root.updatedBy ||
      root.updated_by ||
      null,
    error: null,
  };
};

export const buildRulesPutPayload = (rules, version) => {
  const normalizedRules = rules.map((rule, index) => {
    const match = { ...rule.match };
    if (match.usesSpf === "") {
      delete match.usesSpf;
    } else if (match.usesSpf === "true") {
      match.usesSpf = true;
    } else if (match.usesSpf === "false") {
      match.usesSpf = false;
    }

    Object.keys(match).forEach((key) => {
      if (match[key] === "" || match[key] === null || match[key] === undefined) {
        delete match[key];
      }
    });

    return {
      id: rule.id || `rule-${index + 1}`,
      match,
      recommendedProductIds: normalizeIdList(rule.recommendedProductIds),
      routine: {
        am: normalizeIdList(rule.routine?.am),
        pm: normalizeIdList(rule.routine?.pm),
      },
    };
  });

  const payload = { rules: normalizedRules };
  if (version !== null && version !== undefined && version !== "") {
    payload.version = version;
  }
  return payload;
};

export const validateRulesSet = (rules, knownProductIds = new Set()) => {
  const errors = [];
  if (!Array.isArray(rules) || rules.length === 0) {
    errors.push("Add at least one recommendation rule.");
    return errors;
  }

  rules.forEach((rule, index) => {
    const label = `Rule ${index + 1}`;
    const match = rule.match || {};
    const hasCriterion = [
      match.skinType,
      match.concern,
      match.budget,
      match.ageBand,
      match.sensitivity,
      match.usesSpf,
    ].some((value) => value !== "" && value !== null && value !== undefined);

    if (!hasCriterion) {
      errors.push(`${label}: select at least one quiz answer to match.`);
    }

    const productIds = [
      ...normalizeIdList(rule.recommendedProductIds),
      ...normalizeIdList(rule.routine?.am),
      ...normalizeIdList(rule.routine?.pm),
    ];

    if (productIds.length === 0) {
      errors.push(`${label}: add at least one recommended product or routine step.`);
    }

    if (knownProductIds.size > 0) {
      productIds.forEach((pid) => {
        if (!knownProductIds.has(pid)) {
          errors.push(`${label}: unknown product id "${pid}".`);
        }
      });
    }
  });

  return errors;
};

const normalizeCountMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).map(([key, count]) => ({
    key,
    label: key,
    count: Number(count) || 0,
  }));
};

const normalizeBreakdownList = (value) => {
  if (!Array.isArray(value)) return normalizeCountMap(value);
  return value.map((row) => ({
    key: row.key || row.concern || row.skinType || row.label || row._id || "—",
    label: row.label || row.concern || row.skinType || row.key || "—",
    count: Number(row.count ?? row.total ?? row.value) || 0,
  }));
};

export const parseResultsResponse = (res) => {
  if (!res) {
    return {
      results: [],
      pagination: null,
      totals: { total: 0 },
      countsByConcern: [],
      countsBySkinType: [],
      error: "Empty response",
    };
  }
  if (res.success === false) {
    return {
      results: [],
      pagination: null,
      totals: { total: 0 },
      countsByConcern: [],
      countsBySkinType: [],
      error: res.message || "Failed to load results",
    };
  }

  const data = res.data && typeof res.data === "object" ? res.data : res;
  const resultsCandidates = [
    data.results,
    data.items,
    data.recentResults,
    data.data,
  ];
  const results = (resultsCandidates.find((item) => Array.isArray(item)) || []).map(
    normalizeResultRow
  );

  const totals = data.totals ||
    data.summary || {
      total: data.totalResults ?? data.total ?? results.length,
    };

  return {
    results,
    pagination: normalizePagination(res),
    totals: {
      total: Number(totals.total ?? totals.totalResults ?? totals.count) || results.length,
      ...totals,
    },
    countsByConcern: normalizeBreakdownList(
      data.countsByConcern ?? data.byConcern ?? totals.byConcern
    ),
    countsBySkinType: normalizeBreakdownList(
      data.countsBySkinType ?? data.bySkinType ?? totals.bySkinType
    ),
    error: null,
  };
};

export const normalizeResultRow = (row) => {
  if (!row || typeof row !== "object") return {};
  const answers = row.answers || row.quizAnswers || row;
  return {
    id: row.id || row._id,
    createdAt: row.createdAt || row.created_at,
    skinType: answers.skinType || row.skinType,
    concern: answers.concern || row.concern,
    budget: answers.budget || row.budget,
    ageBand: answers.ageBand || row.ageBand,
    sensitivity: answers.sensitivity || row.sensitivity,
    usesSpf: answers.usesSpf ?? row.usesSpf,
    recommendedProductIds: normalizeIdList(
      row.recommendedProductIds ?? row.productIds ?? row.recommendations
    ),
    channel: row.channel,
  };
};

export const parseLeadsResponse = (res) => {
  if (!res) {
    return { leads: [], pagination: null, error: "Empty response" };
  }
  if (res.success === false) {
    return { leads: [], pagination: null, error: res.message || "Failed to load leads" };
  }

  const data = res.data && typeof res.data === "object" ? res.data : res;
  const listCandidates = [data.leads, data.items, data.data];
  const leads = (listCandidates.find((item) => Array.isArray(item)) || []).map(normalizeLeadRow);

  return {
    leads,
    pagination: normalizePagination(res),
    error: null,
  };
};

export const normalizeLeadRow = (row) => {
  if (!row || typeof row !== "object") return {};
  return {
    id: row.id || row._id,
    name: row.name || "",
    email: row.email || "",
    phone: row.phone || row.mobile || "",
    channel: row.channel || row.source || "",
    consentAt: row.consentAt || row.consentedAt || row.consentTimestamp,
    consentTextVersion: row.consentTextVersion || row.consentVersion || "",
    consentText: row.consentText || "",
    createdAt: row.createdAt || row.created_at,
  };
};

export const maskPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length < 4) return phone || "—";
  if (digits.length <= 6) {
    return `${digits.slice(0, 2)}${"*".repeat(Math.max(0, digits.length - 4))}${digits.slice(-2)}`;
  }
  return `${digits.slice(0, 2)}${"*".repeat(digits.length - 4)}${digits.slice(-2)}`;
};

export const getProductId = (product) =>
  String(product?._id || product?.id || product?.productId || "");

export const buildProductOptions = (products = []) =>
  products
    .map((product) => ({
      value: getProductId(product),
      label: formatProductPickerLabel(product),
    }))
    .filter((option) => option.value);

export const exportLeadsToCSV = (leads) => {
  if (!leads?.length) return null;
  const headers = [
    "Name",
    "Email",
    "Phone",
    "Channel",
    "Consent At",
    "Consent Text Version",
  ];
  const rows = leads.map((lead) => [
    lead.name || "",
    lead.email || "",
    lead.phone || "",
    lead.channel || "",
    lead.consentAt || "",
    lead.consentTextVersion || "",
  ]);
  return [headers, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const text = String(cell ?? "");
          if (text.includes(",") || text.includes('"') || text.includes("\n")) {
            return `"${text.replace(/"/g, '""')}"`;
          }
          return text;
        })
        .join(",")
    )
    .join("\n");
};

export const downloadCSV = (csvContent, filename = "skin_assistant_leads.csv") => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const isSkinAssistantApiNotDeployed = (error) => error?.status === 404;

/** API returns at most this many turns per session in list/detail payloads. */
export const SKIN_ASSISTANT_TURNS_PREVIEW_LIMIT = 10;

/**
 * POST …/knowledge/reindex — PR #71: { success, data: { ok, chunkCount, indexVersion, updatedBy } }
 */
export const parseReindexResponse = (res) => {
  const data = res?.data && typeof res.data === "object" ? res.data : {};
  return {
    ok: Boolean(data.ok),
    chunkCount: Number(data.chunkCount),
    indexVersion: data.indexVersion ?? null,
    updatedBy: data.updatedBy ?? null,
  };
};

export const formatReindexSuccessToast = (stats) => {
  const count = Number(stats?.chunkCount);
  const safeCount = Number.isFinite(count) ? count : 0;
  return `Reindexed: ${safeCount} chunks`;
};

const normalizeUsageTotals = (usageTotals = {}) => ({
  promptTokens: Number(usageTotals.promptTokens) || 0,
  completionTokens: Number(usageTotals.completionTokens) || 0,
  totalTokens: Number(usageTotals.totalTokens) || 0,
  requestCount: Number(usageTotals.requestCount) || 0,
});

export const normalizeChatTurn = (turn, index = 0) => {
  if (!turn || typeof turn !== "object") {
    return { id: `turn-${index}`, role: "user", contentRedacted: "", charCount: 0 };
  }
  const role = turn.role === "assistant" ? "assistant" : "user";
  const contentRedacted = turn.contentRedacted ?? turn.content_redacted ?? "";
  return {
    id: turn.id || turn._id || `turn-${index}`,
    role,
    contentRedacted: String(contentRedacted),
    charCount: Number(turn.charCount ?? turn.char_count) || 0,
  };
};

/** Turn list for UI — only `turns[]`, never legacy messages/transcript fields. */
export const getSessionTurns = (session) => {
  if (!session) return [];
  return Array.isArray(session.turns) ? session.turns : [];
};

/** Display count: `turnCount` from API, else length of `turns`. */
export const getSessionTurnCount = (session) => {
  if (!session) return 0;
  const fromApi = Number(session.turnCount ?? session.turn_count);
  if (Number.isFinite(fromApi) && fromApi >= 0) {
    return fromApi;
  }
  return getSessionTurns(session).length;
};

export const mergeSessionAfterRefetch = (listRow, refetched) => {
  if (!refetched) return listRow;
  const listTurns = getSessionTurns(listRow);
  const refetchedTurns = getSessionTurns(refetched);
  const turns = refetchedTurns.length > 0 ? refetchedTurns : listTurns;
  const turnCount = getSessionTurnCount(refetched) || getSessionTurnCount(listRow) || turns.length;
  return {
    ...listRow,
    ...refetched,
    turns,
    turnCount,
  };
};

export const normalizeChatSession = (row) => {
  if (!row || typeof row !== "object") return {};
  const userId = row.userId ?? row.user_id ?? null;
  const turns = Array.isArray(row.turns)
    ? row.turns.map((turn, index) => normalizeChatTurn(turn, index))
    : [];
  const turnCountRaw = row.turnCount ?? row.turn_count;
  const turnCount = Number.isFinite(Number(turnCountRaw))
    ? Number(turnCountRaw)
    : turns.length;

  return {
    sessionId: row.sessionId || row.session_id || "",
    userId: userId === undefined ? null : userId,
    resultId: row.resultId ?? row.result_id ?? null,
    turnCount,
    turns,
    usageTotals: normalizeUsageTotals(row.usageTotals),
    lastModel: row.lastModel ?? null,
    blockedCount: Number(row.blockedCount) || 0,
    updatedAt: row.updatedAt || row.updated_at,
    createdAt: row.createdAt || row.created_at,
    isGuest: userId === null || userId === undefined || userId === "",
  };
};

/**
 * GET …/chats — PR #71: data.sessions + data.pagination
 */
export const parseChatsResponse = (res) => {
  if (!res) {
    return { sessions: [], pagination: null, error: "Empty response" };
  }
  if (res.success === false) {
    return { sessions: [], pagination: null, error: res.message || "Failed to load chats" };
  }

  const data =
    res.data && typeof res.data === "object" && !Array.isArray(res.data) ? res.data : res;
  const rawSessions = Array.isArray(data.sessions)
    ? data.sessions
    : Array.isArray(res.sessions)
      ? res.sessions
      : [];
  const sessions = rawSessions.map((session) => normalizeChatSession(session));

  const pagination = data.pagination
    ? {
        page: Number(data.pagination.page) || 1,
        pages: Number(data.pagination.pages) || 1,
        total: Number(data.pagination.total) || 0,
        limit: Number(data.pagination.limit) || 10,
      }
    : normalizePagination(res);

  return {
    sessions,
    pagination,
    error: null,
  };
};

export const sessionShowsLastTenTurnsNote = (session) => {
  if (!session) return false;
  const turnCount = Number(session.turnCount) || 0;
  const shown = session.turns?.length || 0;
  return turnCount > shown || turnCount > SKIN_ASSISTANT_TURNS_PREVIEW_LIMIT;
};

export const formatChatParticipant = (session) => {
  if (!session) return "—";
  if (session.isGuest) return "Guest";
  if (session.userId) return `User ${String(session.userId)}`;
  return "User";
};

export const formatChatParticipantSubline = (session) => {
  if (!session?.sessionId) return "";
  const parts = [`Session ${session.sessionId}`];
  if (session.resultId) parts.push(`Result ${session.resultId}`);
  return parts.join(" · ");
};

export const labelForOption = (options, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const found = options.find((opt) => opt.value === value);
  return found?.label || String(value);
};
